import React, { useState } from 'react';
import { useQuestions } from '../../Context/QuestionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, Form, InputGroup, Table, Badge, Card, Row, Col } from 'react-bootstrap';
import { getMediaUrl } from '../../Services/apiService';

const DifficultyBadge = ({ level }) => {
  const variant = {
    EASY: 'success',
    MEDIUM: 'warning',
    HARD: 'danger'
  };
  
  return (
    <Badge bg={variant[level] || 'secondary'} className="px-2 py-1">
      {level === 'EASY' ? 'BASİT' : level === 'MEDIUM' ? 'ORTA' : 'ZOR'}
    </Badge>
  );
};

const QuestionList = () => {
  const { questions, deleteQuestion, loading } = useQuestions();
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const filteredQuestions = questions.filter(q => {
    const matchesFilter = filter === 'ALL' || q.difficulty === filter;
    const matchesSearch = q.text.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) return <div className="p-5 text-center text-muted">Sorular yükleniyor...</div>;

  return (
    <div className="py-2">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Soru Bankası</h2>
          <p className="text-muted mb-0">Yarışma sorularını yönetin</p>
        </div>
        <Link to="/questions/new">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button variant="primary" className="d-flex align-items-center gap-2 shadow-sm border-0">
              <Plus size={18} />
              Yeni Soru Ekle
            </Button>
          </motion.div>
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-4 custom-card">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0">
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Soru ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border-start-0 ps-0 shadow-none"
                />
              </InputGroup>
            </Col>
            
            <Col md={6} className="d-flex gap-2 justify-content-md-end">
              {['ALL', 'EASY', 'MEDIUM', 'HARD'].map((level) => (
                <Button
                  key={level}
                  variant={filter === level ? 'dark' : 'light'}
                  size="sm"
                  onClick={() => setFilter(level)}
                  className="fw-medium border-0"
                >
                  {level === 'ALL' ? 'Tümü' : level === 'EASY' ? 'Basit' : level === 'MEDIUM' ? 'Orta' : 'Zor'}
                </Button>
              ))}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table List */}
      <Card className="custom-card overflow-hidden">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="bg-light">
            <tr>
              <th className="border-0 ps-4 py-3 text-secondary small text-uppercase" style={{width: '60px'}}>ID</th>
              <th className="border-0 py-3 text-secondary small text-uppercase" style={{width: '40%'}}>Soru Metni</th>
              <th className="border-0 py-3 text-secondary small text-uppercase">Zorluk</th>
              <th className="border-0 py-3 text-secondary small text-uppercase">Tip</th>
              <th className="border-0 py-3 text-secondary small text-uppercase">Medya</th>
              <th className="border-0 py-3 text-secondary small text-uppercase">Cevap</th>
              <th className="border-0 py-3 text-secondary small text-uppercase text-end pe-4">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
            {filteredQuestions.map((q) => (
              <motion.tr
                key={q.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                layout
                className="bg-white"
              >
                <td className="ps-4 fw-bold text-muted">#{q.id}</td>
                <td className="fw-medium text-dark">{q.text}</td>
                <td><DifficultyBadge level={q.difficulty} /></td>
                <td>
                  <Badge
                    bg={q.type === 'MULTIPLE_CHOICE' ? 'primary' : 'secondary'}
                    style={{fontSize: '0.75rem', fontWeight: 500}}
                  >
                    {q.type === 'MULTIPLE_CHOICE' ? 'Test Sorusu' : 'Klasik Soru'}
                  </Badge>
                </td>
                <td>
                    {q.media ? (
                        <div className="d-flex align-items-center justify-content-center bg-light rounded border" style={{width: 32, height: 32, overflow: 'hidden'}}>
                            {q.media.type === 'IMAGE' ? (
                                <img src={getMediaUrl(q.media.url)} alt="media" className="w-100 h-100 object-fit-cover" />
                            ) : (
                                <Badge bg="secondary" style={{fontSize: '0.5rem'}}>VID</Badge>
                            )}
                        </div>
                    ) : (
                        <span className="text-muted small ms-2">-</span>
                    )}
                </td>
                <td className="text-success fw-bold font-monospace">{q.correctAnswer}</td>
                <td className="text-end pe-4">
                  <div className="d-flex gap-2 justify-content-end">
                     <Button variant="light" size="sm" className="text-primary border-0 bg-transparent p-1">
                        <Edit2 size={16} />
                     </Button>
                     <Button 
                        variant="light" 
                        size="sm" 
                        className="text-danger border-0 bg-transparent p-1"
                        onClick={() => deleteQuestion(q.id)}
                     >
                        <Trash2 size={16} />
                     </Button>
                  </div>
                </td>
              </motion.tr>
            ))}
            </AnimatePresence>
          </tbody>
        </Table>
        
        {filteredQuestions.length === 0 && (
          <div className="text-center py-5">
            <Filter size={48} className="text-muted opacity-25 mb-3" />
            <p className="text-muted fw-medium">Aradığınız kriterlere uygun soru bulunamadı.</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default QuestionList;
